import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import DoramaCard from '../components/DoramaCard';
import CustomSelect from '../components/CustomSelect';
import { useGetHistoryQuery } from '../services/historyApi';
import styles from '../css/History.module.css';

const PERIOD_OPTIONS = [
  { value: 'all',   label: 'Всё время' },
  { value: 'week',  label: 'За неделю' },
  { value: 'month', label: 'За месяц' },
];

const History = () => {
  const [filterPeriod, setFilterPeriod] = useState('all');
  const navigate = useNavigate();

  const isAuthenticated = useSelector(s => s.auth.isAuthenticated);

  const { data, isLoading } = useGetHistoryQuery({}, {
    skip: !isAuthenticated,
    refetchOnMountOrArgChange: true,
  });

  const allItems = data?.data ?? [];
  const total = data?.meta?.total ?? 0;

  const filterItems = () => {
    const now = new Date();
    const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);

    return allItems.filter(item => {
      const watchedDate = new Date(item.last_watched_at);
      if (filterPeriod === 'week') return watchedDate >= weekAgo;
      if (filterPeriod === 'month') return watchedDate >= monthAgo;
      return true;
    });
  };

  const filteredItems = filterItems();

  if (!isAuthenticated) {
    return (
      <div className={styles.historyPage}>

        <div className={styles.guestState}>
          <div className={styles.guestIcon}>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/>
              <polyline points="10 17 15 12 10 7"/>
              <line x1="15" y1="12" x2="3" y2="12"/>
            </svg>
          </div>

          <div className={styles.guestCopy}>
            <p className={styles.guestEyebrow}>История просмотров</p>
            <h1 className={styles.guestTitle}>Войдите, чтобы продолжить с того места, где остановились</h1>
            <p className={styles.guestText}>
              После входа здесь появятся просмотренные дорамы, прогресс по сериям и быстрый доступ к тому, что вы смотрели недавно.
            </p>
          </div>

          <div className={styles.guestFeatures}>
            <div className={styles.guestFeature}>
              <span className={styles.guestFeatureDot} />
              Сохранение прогресса по сериям
            </div>
            <div className={styles.guestFeature}>
              <span className={styles.guestFeatureDot} />
              Быстрое возвращение к просмотру
            </div>
            <div className={styles.guestFeature}>
              <span className={styles.guestFeatureDot} />
              История за неделю, месяц и всё время
            </div>
          </div>

          <div className={styles.guestActions}>
            <Link to="/auth/login" className={styles.loginButton}>
              Войти
            </Link>
            <Link to="/" className={styles.secondaryButton}>
              На главную
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className={styles.historyPage}>
        <p>Загрузка...</p>
      </div>
    );
  }

  return (
    <div className={styles.historyPage}>
      <button className={styles.backButton} onClick={() => navigate(-1)}>
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M19 12H5M12 19l-7-7 7-7"/>
        </svg>
        Назад
      </button>

      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <h1 className={styles.title}>История просмотров</h1>
          <p className={styles.subtitle}>
            Всего просмотрено: <span className={styles.count}>{total}</span> дорам
          </p>
        </div>

        <div className={styles.filterGroup}>
          <CustomSelect
            value={filterPeriod}
            options={PERIOD_OPTIONS}
            onChange={setFilterPeriod}
          />
        </div>
      </div>

      {filteredItems.length > 0 ? (
        <div className={styles.dramasGrid}>
          {filteredItems.map(item => (
            <div key={item.material_id} className={styles.dramaWrapper}>
              <DoramaCard
                id={item.material_id}
                slug={item.slug}
                slug_id={item.slug_id}
                image={
                  item.poster_url
                    ? `https://doramahype.ru/uploads/posters/${item.poster_url}`
                    : null
                }
                title={item.title}
                year={item.year}
                genres={item.genres ?? []}
                fluid
              />
              <div className={styles.progressInfo}>
                <div className={styles.progressBar}>
                  <div
                    className={styles.progressFill}
                    style={{
                      width: item.episodes_count
                        ? `${Math.min((item.episodes_watched / item.episodes_count) * 100, 100)}%`
                        : '100%',
                    }}
                  />
                </div>
                <p className={styles.progressText}>
                  {item.episodes_count
                    ? `${item.episodes_watched} / ${item.episodes_count} серий`
                    : 'Просмотрено'}
                </p>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className={styles.emptyState}>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10"/>
            <line x1="12" y1="8" x2="12" y2="12"/>
            <line x1="12" y1="16" x2="12.01" y2="16"/>
          </svg>
          <p>
            {total > 0
              ? 'За выбранный период просмотров не найдено'
              : 'История просмотров пуста'}
          </p>
        </div>
      )}
    </div>
  );
};

export default History;
